import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Building2, Clock, Loader2 } from "lucide-react";
import { BrandMark } from "@/components/brand-mark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { usePageTitle } from "@/hooks/use-page-title";
import { useOnboardingActions, useOnboardingState } from "@/hooks/queries/use-onboarding";
import { useRenameOrganization } from "@/hooks/queries/use-organization";
import { useAuthStore } from "@/stores/auth-store";
import { PlanStep } from "@/components/onboarding/plan-step";
import { AiSetupStep } from "@/components/onboarding/ai-setup-step";
import { GitHubSetupStep } from "@/components/onboarding/github-step";

function errorText(error: unknown): string | null {
  if (!error) return null;
  return error instanceof Error ? error.message : "Something went wrong. Please try again.";
}

/** One-field "name your organization" form, shared by every naming step. */
function OrganizationNameForm({
  initialName,
  submitLabel,
  pending,
  error,
  onSubmit,
}: {
  initialName: string;
  submitLabel: string;
  pending: boolean;
  error: unknown;
  onSubmit: (name: string) => void;
}) {
  const [name, setName] = useState(initialName);
  const message = errorText(error);
  return (
    <form
      className="w-full space-y-6"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim()) onSubmit(name.trim());
      }}
    >
      <Input
        aria-label="Organization name"
        autoFocus
        maxLength={100}
        value={name}
        onChange={(e) => {
          setName(e.target.value);
        }}
      />
      {message && (
        <p className="text-sm text-red-400" role="alert">
          {message}
        </p>
      )}
      <div className="flex justify-center">
        <Button type="submit" disabled={pending || !name.trim()} className="min-w-32">
          {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : submitLabel}
        </Button>
      </div>
    </form>
  );
}

function Heading({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="space-y-2 text-center">
      <h1 className="text-3xl font-semibold tracking-tight text-foreground">{title}</h1>
      <p className="text-sm text-muted-foreground">{subtitle}</p>
    </div>
  );
}

/**
 * First-run screen for self-serve users (/welcome). Depending on the server's
 * onboarding state it asks the admin of a new organization to confirm its
 * name and then pick a plan, offers "request to join / create my own" to someone whose company
 * domain another organization holds, or asks an org-less user to create one.
 * When nothing is left to do it sends the user on to the app.
 */
export function WelcomePage() {
  usePageTitle("Welcome");
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, logout } = useAuthStore();
  const { data: state, isLoading, error } = useOnboardingState();
  const actions = useOnboardingActions();
  const rename = useRenameOrganization();
  const [creatingOwn, setCreatingOwn] = useState(false);

  if (!authLoading && !isAuthenticated) return <Navigate to="/login" replace />;
  if (state?.step === "ready") return <Navigate to="/dashboard" replace />;

  let body: React.ReactNode;
  if (isLoading || authLoading || !state) {
    body = error ? (
      <p className="text-sm text-red-400" role="alert">
        {errorText(error)}
      </p>
    ) : (
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading" />
    );
  } else if (state.step === "name_organization") {
    body = (
      <>
        <Heading title="Welcome to CompBox" subtitle="Enter your organization name to get started." />
        <OrganizationNameForm
          initialName={state.suggestedName}
          submitLabel="Continue"
          pending={rename.isPending}
          error={rename.error}
          onSubmit={(name) => {
            // Renaming refetches the onboarding state: the plan step (or the
            // app, once nothing is left to do) follows on its own.
            rename.mutate(name);
          }}
        />
      </>
    );
  } else if (state.step === "choose_plan") {
    body = <PlanStep organizationName={state.organizationName} people={state.people} pricing={state.pricing} />;
  } else if (state.step === "choose_ai") {
    body = <AiSetupStep organizationName={state.organizationName} isAdmin={state.organizationRole === "admin"} />;
  } else if (state.step === "connect_github") {
    body = <GitHubSetupStep organizationName={state.organizationName} installUrl={state.installUrl} />;
  } else if (state.step === "create_organization" || creatingOwn) {
    body = (
      <>
        <Heading title="Create your organization" subtitle="Enter your organization name to get started." />
        <OrganizationNameForm
          initialName={state.suggestedName}
          submitLabel="Create organization"
          pending={actions.createOrganization.isPending}
          error={actions.createOrganization.error}
          onSubmit={(name) => {
            actions.createOrganization.mutate(name);
          }}
        />
        {creatingOwn && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setCreatingOwn(false);
            }}
          >
            Back
          </Button>
        )}
      </>
    );
  } else {
    const orgName = state.organization.name;
    const request = state.request;
    const busy = actions.requestToJoin.isPending || actions.cancelRequest.isPending;
    const message = errorText(actions.requestToJoin.error ?? actions.cancelRequest.error);

    if (request?.status === "pending") {
      body = (
        <>
          <Clock className="h-10 w-10 text-primary" aria-hidden />
          <Heading
            title="Request sent"
            subtitle={`We let the admins of ${orgName} know. You'll get an email as soon as they respond.`}
          />
          <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => {
                actions.cancelRequest.mutate();
              }}
            >
              Withdraw request
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setCreatingOwn(true);
              }}
            >
              Create my own organization instead
            </Button>
          </div>
        </>
      );
    } else {
      const declined = request?.status === "declined";
      body = (
        <>
          <Building2 className="h-10 w-10 text-primary" aria-hidden />
          <Heading
            title={declined ? "Request declined" : `${orgName} is already on CompBox`}
            subtitle={
              declined
                ? `An admin of ${orgName} declined your request. You can create your own organization, or ask an admin there for an invitation.`
                : `Your email address belongs to ${orgName}. Ask to join them, or start your own organization.`
            }
          />
          <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            {!declined && (
              <Button
                disabled={busy}
                onClick={() => {
                  actions.requestToJoin.mutate();
                }}
              >
                {state.joinMode === "auto" ? `Join ${orgName}` : "Request to join"}
              </Button>
            )}
            <Button
              variant={declined ? "default" : "outline"}
              onClick={() => {
                setCreatingOwn(true);
              }}
            >
              Create my own organization
            </Button>
          </div>
          {message && (
            <p className="text-sm text-red-400" role="alert">
              {message}
            </p>
          )}
        </>
      );
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center bg-background px-4 py-10">
      <BrandMark />
      <div
        className={`flex w-full flex-1 flex-col items-center justify-center gap-8 py-10 ${
          state?.step === "choose_plan" || state?.step === "choose_ai" ? "max-w-xl" : "max-w-md"
        }`}
      >
        {body}
      </div>
      <button
        type="button"
        className="text-xs text-muted-foreground hover:underline"
        onClick={() => {
          logout();
          void navigate("/login", { replace: true });
        }}
      >
        Sign out
      </button>
    </div>
  );
}
